"use client";

import type { FC, ReactNode } from "react";
import { createContext, use, useMemo } from "react";
import { useFibbl, type UseFibblReturnType } from "./useFibbl";

type FibblConfigContextType = Pick<UseFibblReturnType, "sku" | "isFibblEnabled">;

type FibblStateContextType = Pick<
    UseFibblReturnType,
    "isFibblReady" | "isVtoActive" | "layerRef" | "openVto" | "closeVto"
>;

const FibblConfigContext = createContext<FibblConfigContextType | null>(null);
const FibblStateContext = createContext<FibblStateContextType | null>(null);

interface FibblProviderProps {
    sku: string;
    children: ReactNode;
}

export const FibblProvider: FC<FibblProviderProps> = ({ sku, children }) => {
    const { isFibblEnabled, isFibblReady, isVtoActive, layerRef, openVto, closeVto } = useFibbl({
        sku,
    });

    const configValue = useMemo(
        () => ({
            sku,
            isFibblEnabled,
        }),
        [sku, isFibblEnabled]
    );

    const stateValue = useMemo(
        () => ({ isFibblReady, isVtoActive, layerRef, openVto, closeVto }),
        [isFibblReady, isVtoActive, layerRef, openVto, closeVto]
    );

    return (
        <FibblConfigContext value={configValue}>
            <FibblStateContext value={stateValue}>{children}</FibblStateContext>
        </FibblConfigContext>
    );
};

export const useFibblConfig = () => {
    const context = use(FibblConfigContext);
    if (!context) {
        throw new Error("useFibblConfig must be used within a FibblProvider");
    }
    return context;
};

export const useFibblState = () => {
    const context = use(FibblStateContext);
    if (!context) {
        throw new Error("useFibblState must be used within a FibblProvider");
    }
    return context;
};
